import { Card, Chip, Box, Typography } from "@mui/material";
import styled, { css } from "styled-components";
import { SPACING, ANIMATION } from "../../utils/constants";
import { StatusChipProps, StyledCardProps } from "./types";

export const cardFocusRing = css`
  outline: 3px solid var(--color-primary);
  outline-offset: 2px;
`;

export const StyledCard = styled(Card)<StyledCardProps>`
  && {
    border-radius: 16px;
    box-shadow: var(--shadow-large);
    border: 1px solid var(--color-card-border);
    transition:
      transform ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut},
      box-shadow ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut},
      border-color ${ANIMATION.duration.normal} ${ANIMATION.easing.easeOut};
    background: var(--color-card-background);
    cursor: ${({ $cursor }) => ($cursor ? "pointer" : "default")};
    margin-top: ${SPACING.xs}px;

    /* Minimum touch target size for mobile */
    min-height: 44px;

    &:hover {
      transform: translateY(-4px);
      box-shadow: var(--shadow-large-2);
      border-color: var(--color-primary);
    }

    &:focus-visible {
      ${cardFocusRing}
    }

    &:active {
      transform: translateY(-2px);
      transition: transform ${ANIMATION.duration.instant}
        ${ANIMATION.easing.easeOut};
    }
  }
`;

export const CardHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${SPACING.md}px;
  min-height: 76px;
`;

export const HeaderLeft = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.sm + SPACING.xs}px;
  flex: 1;
  min-width: 0;
`;

export const CardIcon = styled(Box)`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--color-primary-color-background);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-bottom: ${SPACING.sm + SPACING.xs}px;
  flex-shrink: 0;
`;

export const MetricsGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SPACING.md}px;
  margin-top: ${SPACING.md + SPACING.xs}px;
`;

export const MetricBox = styled(Box)`
  padding: ${SPACING.sm + SPACING.xs}px;
  border-radius: 8px;
  background: var(--color-card-metric-background);
  border: 1px solid var(--color-card-metric-border);
`;

export const StatusChip = styled(Chip)<StatusChipProps>`
  && {
    height: 24px;
    font-size: var(--text-xs);
    font-weight: 600;
    border-radius: 12px;
    color: ${({ $color, $isActive }) =>
      $color || ($isActive ? "var(--color-success)" : "var(--color-gray-600)")};
    background-color: ${({ $color, $isActive }) => {
      const c =
        $color ||
        ($isActive ? "var(--color-success)" : "var(--color-gray-600)");
      return `color-mix(in srgb, ${c} 12%, transparent)`;
    }};
    border: 1px solid
      ${({ $color, $isActive }) => {
        const c =
          $color ||
          ($isActive ? "var(--color-success)" : "var(--color-gray-600)");
        return `color-mix(in srgb, ${c} 30%, transparent)`;
      }};
  }
`;

export const NameTypeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: -${SPACING.md}px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

export const IconStatusContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`;

export const CompactStatusChip = styled(StatusChip)`
  && {
    font-size: var(--text-xs);
    height: 22px;
    padding: 0 ${SPACING.sm}px;

    .MuiChip-label {
      padding: 0 ${SPACING.sm}px;
      white-space: nowrap;
    }
  }
`;

export const TitleSubtitleWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

export const CardTitle = styled(Typography)`
  && {
    font-weight: 700;
    color: var(--color-text-default);
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.3;
    width: 100%;
  }
`;

export const CardSubtitle = styled(Typography)`
  && {
    color: var(--color-text-secondary);
    font-size: var(--text-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
  }
`;
